// Downloads an API-generated file by POSTing JSON and streaming the response to a Blob.
// Avoids base64 expansion and large JS string allocations.
//
// Errors are thrown as plain strings so Blazor's JSException.Message is just the message.
// A lost connection - the request never reaching the server, or the server aborting a stream
// partway through - is thrown as DOWNLOAD_INTERRUPTED, which DataFilesModal maps to a translated
// message instead of showing the browser's "TypeError: network error" text.
window.DOWNLOAD_INTERRUPTED = 'download-interrupted';

window.downloadFileFromPost = async function (url, payload, filenameFallback, abortKey) {
    const controller = new AbortController();

    if (!window.__downloadAbortControllers) {
        window.__downloadAbortControllers = new Map();
    }

    if (abortKey) {
        try {
            // Replace any existing controller for this key
            const prev = window.__downloadAbortControllers.get(abortKey);
            if (prev) prev.abort();
        } catch {
            // ignore
        }
        window.__downloadAbortControllers.set(abortKey, controller);
    }

    let response;
    try {
        response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload),
            signal: controller.signal
        });
    } catch (err) {
        if (err && err.name === 'AbortError') {
            throw 'Download cancelled.';
        }
        throw window.DOWNLOAD_INTERRUPTED;
    }

    if (!response.ok) {
        if (abortKey && window.__downloadAbortControllers) {
            window.__downloadAbortControllers.delete(abortKey);
        }

        if (response.status === 401) {
            throw new Error('You must be signed in to download this file. Please sign in and try again.');
        }

        if (response.status === 403) {
            throw new Error('You are not authorized to download this file.');
        }

        let message = `An error occurred while generating the file. (HTTP ${response.status})`;
        try {
            const text = await response.text();
            if (text) {
                try {
                    const problem = JSON.parse(text);
                    if (problem && problem.message) message = problem.message;
                    else message = text;
                } catch {
                    message = text;
                }
            }
        } catch {
            // ignore
        }
        // Throw a plain string so Blazor's `JSException.Message` is just the message,
        // not duplicated with a stack trace.
        throw message;
    }

    const contentDisposition = response.headers.get('content-disposition') || '';
    const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
    const filename = (match && match[1]) ? match[1] : filenameFallback;

    // Stream response to avoid buffering the entire file before cancel can take effect.
    // We only trigger the download if the stream completes.
    try {
        if (!response.body || !response.body.getReader) {
            // Fallback for older browsers
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();

            URL.revokeObjectURL(objectUrl);
            return;
        }

        const reader = response.body.getReader();
        const chunks = [];
        let totalLength = 0;

        while (true) {
            // If aborted, stop before we build and save the file.
            if (controller.signal.aborted) {
                try { reader.cancel(); } catch { }
                throw 'Download cancelled.';
            }

            const { done, value } = await reader.read();

            if (done) break;

            if (value) {
                chunks.push(value);
                totalLength += value.length;
            }
        }

        if (controller.signal.aborted) {
            throw 'Download cancelled.';
        }

        const blob = new Blob(chunks);
        const objectUrl = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        URL.revokeObjectURL(objectUrl);
    } catch (err) {
        if (err && err.name === 'AbortError') {
            throw 'Download cancelled.';
        }
        // Strings are our own messages ('Download cancelled.') - pass them through. A real Error
        // here means the stream broke partway: the server aborted it or the connection dropped.
        // Nothing was saved, because the file is only written once the stream completes.
        if (err instanceof Error) {
            throw window.DOWNLOAD_INTERRUPTED;
        }
        throw err;
    } finally {
        if (abortKey && window.__downloadAbortControllers) {
            window.__downloadAbortControllers.delete(abortKey);
        }
    }
};

// Cancel a download started by downloadFileFromPost.
window.cancelDownloadFromPost = function (abortKey) {
    try {
        const map = window.__downloadAbortControllers;
        if (!map) return;
        const controller = map.get(abortKey);
        if (controller) {
            controller.abort();
            map.delete(abortKey);
        }
    } catch {
        // ignore
    }
};
